import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastAction {
  label: string;
  callback: () => void;
}

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration: number;        // ms (0 = sticky)
  dedupKey?: string;
  action?: ToastAction;
  createdAt: number;
}

/**
 * GLOBAL TOAST SERVICE
 * - Dedup window: identical messages/keys within 3s are silently dropped
 * - Max 5 concurrent toasts; oldest auto-evicted
 * - Toasts are rendered by <app-toast-container> (bottom-right)
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
  private _toasts$ = new BehaviorSubject<Toast[]>([]);
  toasts$ = this._toasts$.asObservable();

  private readonly DEDUP_WINDOW_MS = 3000;
  private readonly MAX_VISIBLE = 5;
  private recentKeys = new Map<string, number>();

  /** Generic toast */
  show(input: Omit<Toast, 'id' | 'createdAt' | 'duration'> & { duration?: number }): string | null {
    const key = input.dedupKey || input.message;
    const now = Date.now();
    const lastShown = this.recentKeys.get(key);

    if (lastShown && now - lastShown < this.DEDUP_WINDOW_MS) {
      return null; // Silent drop — dedup
    }
    this.recentKeys.set(key, now);
    this.purgeOldKeys(now);

    const id = this.makeId();
    const toast: Toast = {
      id,
      type: input.type,
      message: input.message,
      duration: input.duration ?? this.defaultDuration(input.type),
      dedupKey: input.dedupKey,
      action: input.action,
      createdAt: now
    };

    let next = [...this._toasts$.value, toast];
    // Evict oldest if exceeding cap
    if (next.length > this.MAX_VISIBLE) {
      next = next.slice(next.length - this.MAX_VISIBLE);
    }
    this._toasts$.next(next);

    if (toast.duration > 0) {
      setTimeout(() => this.dismiss(id), toast.duration);
    }
    return id;
  }

  success(message: string, action?: ToastAction): void {
    this.show({ type: 'success', message, action });
  }
  error(message: string, action?: ToastAction): void {
    this.show({ type: 'error', message, action });
  }
  warning(message: string, action?: ToastAction): void {
    this.show({ type: 'warning', message, action });
  }
  info(message: string, action?: ToastAction): void {
    this.show({ type: 'info', message, action });
  }

  dismiss(id: string): void {
    this._toasts$.next(this._toasts$.value.filter(t => t.id !== id));
  }

  dismissAll(): void {
    this._toasts$.next([]);
  }

  private defaultDuration(type: ToastType): number {
    switch (type) {
      case 'success': return 3000;
      case 'info': return 4000;
      case 'warning': return 5000;
      case 'error': return 7000;
    }
  }

  private makeId(): string {
    return 't-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 7);
  }

  private purgeOldKeys(now: number): void {
    if (this.recentKeys.size < 20) return;
    for (const [k, t] of this.recentKeys) {
      if (now - t > this.DEDUP_WINDOW_MS * 3) this.recentKeys.delete(k);
    }
  }
}
